import { routeName } from '../../routes/route-constants';
import { ApplicationState } from 'app/types-store';
import { FavouriteDefinition, ReportDefinition } from 'app/types-shared';
import { getCaseTypeIds } from 'app/area/reports/caseTypeIds';
import { fieldValueToFieldItem } from 'app/area/case-report/components/included-domains/map-to-field-items';
import { createSelector } from 'reselect';

export const isValidFavouriteLink = (pathname: string) => {
  return new RegExp(`${routeName.Favourite}\/[0-9]`).test(pathname);
};

const getIncludedFields = (instanceKey: string, state: ApplicationState) => {
  return selectFromSelectedReport(instanceKey)(state, r => r?.includedFields);
};

export const selectFavouriteInstanceState = (instanceKey: string) => (state: ApplicationState) =>
  state?.favourite?.instances?.[instanceKey];

export const selectDefinition = (instanceKey: string) => (state: ApplicationState) =>
  selectFavouriteInstanceState(instanceKey)(state)?.definition;

export const selectWorkingDefinition = (instanceKey: string) => (state: ApplicationState) =>
  selectFavouriteInstanceState(instanceKey)(state)?.workingDefinition;

export const selectFetchingReportCategory = (instanceKey: string) => (state: ApplicationState) =>
  selectFavouriteInstanceState(instanceKey)(state)?.fetchingReportCategory;

export const selectReportCategory = (instanceKey: string) => (state: ApplicationState) =>
  selectFavouriteInstanceState(instanceKey)(state)?.reportCategory;

export const selectFetchingDefinition = (instanceKey: string) => (state: ApplicationState) =>
  selectFavouriteInstanceState(instanceKey)(state)?.fetchingDefinition;

export const selectScheduleDefinitionId = (instanceKey: string) => (state: ApplicationState) =>
  selectFavouriteInstanceState(instanceKey)(state)?.definition?.scheduleDefinitionId;

export const selectFetchingSortOptions = (instanceKey: string) => (state: ApplicationState) =>
  selectFavouriteInstanceState(instanceKey)(state)?.fetchingSortOptions;

export const selectSortOptions = (instanceKey: string) => (state: ApplicationState) =>
  selectFavouriteInstanceState(instanceKey)(state)?.sortOptions;

export const selectFromWorkingDefinition =
  (instanceKey: string) => <T>(state: ApplicationState, selector: (def: FavouriteDefinition) => T) =>
    selector(selectWorkingDefinition(instanceKey)(state));

const selectFromSelectedReport =
  (instanceKey: string) => <T>(state: ApplicationState, selector: (def: ReportDefinition) => T) => {
    const wd = selectWorkingDefinition(instanceKey)(state);
    return selector(wd?.reports?.find(r => r?.report?.v === wd?.selectedReport?.v));
  };

export const selectSelectedReport =
  (instanceKey: string) => (state: ApplicationState) =>
    selectFromWorkingDefinition(instanceKey)(state, def => def?.selectedReport);

export const selectAggregationForSelectedReport = (instanceKey: string) => createSelector(
  (state: ApplicationState) => selectFromSelectedReport(instanceKey)(state, r => r),
  (report) => ({
    aggregationDomain: report?.aggregationDomain,
    aggregationType: report?.aggregationType,
    uomSetDomain: report?.uomSetDomain
  })
);

export const selectTargetApplicationForSelectedReport = (instanceKey: string) => (state: ApplicationState) =>
  selectFromSelectedReport(instanceKey)(state, r => r?.targetApp);

export const selectExposure = (instanceKey: string) => (state: ApplicationState) =>
  selectFromWorkingDefinition(instanceKey)(state, def => ({
    exposureApplication: def?.exposureApplication,
    exposureFactor: def?.exposureFactor,
    exposurePercent: def?.exposurePercent
  }));

export const selectSelectionsForSelectedReport = (instanceKey: string) => createSelector(
  (state: ApplicationState) => selectFromSelectedReport(instanceKey)(state, r => r),
  (report) => ({
    rollingAverage: report?.rollingAverage,
    selections: report?.selections
  })
);

export const selectPredefDateForSelectedReport = (instanceKey: string) => (state: ApplicationState) =>
  selectFromSelectedReport(instanceKey)(state, r => r?.predefDate);

export const selectLayoutTypeForSelectedReport = (instanceKey: string) => (state: ApplicationState) =>
  selectFromSelectedReport(instanceKey)(state, r => r?.layoutType);

export const selectLayoutAttributesForSelectedReport = (instanceKey: string) => (state: ApplicationState) =>
  selectFromSelectedReport(instanceKey)(state, r => r?.layoutAttributes);

export const selectFavouriteId = (instanceKey: string) => (state: ApplicationState) =>
  selectFromWorkingDefinition(instanceKey)(state, def => def?.favourite?.v);

export const selectSearchRequiredUsers = (instanceKey: string) => (state: ApplicationState) =>
  selectFavouriteInstanceState(instanceKey)(state)?.searchRequiredUsers;

export const selectUrlMultipleSuppliers = (instanceKey: string) => (state: ApplicationState) =>
  selectFavouriteInstanceState(instanceKey)(state)?.urlMultipleSuppliers;

export const selectPresentationTypeForSelectedReport = (instanceKey: string) => (state: ApplicationState) =>
  selectFromSelectedReport(instanceKey)(state, r => r?.presentationType?.v);

export const selectValidateFavouriteFilters = (instanceKey: string) => (state: ApplicationState) =>
  selectFavouriteInstanceState(instanceKey)(state)?.validateFilters;

export const selectDrillDownReportForSelectedReport = (instanceKey: string) => (state: ApplicationState) =>
  selectFromSelectedReport(instanceKey)(state, r => r?.drillDownReport);

export const selectWorkingDefenitionIncludedDomains = (instanceKey: string) => (state: ApplicationState) => {
  const includedFields = getIncludedFields(instanceKey, state);
  return includedFields.map((data) => fieldValueToFieldItem(data.domain, null, data.entityTypePath));
};

export const selectCaseTypeIds = (instanceKey: string) => (state: ApplicationState) =>
  getCaseTypeIds(selectFavouriteInstanceState(instanceKey)(state)?.workingDefinition);

export const selectDrillDownLayoutTypeForSelectedReport = (instanceKey: string) => (state: ApplicationState) =>
  selectFromSelectedReport(instanceKey)(state, r => r?.drillDownLayoutType);

export const selectSortOrderForSelectedReport = (instanceKey: string) => (state: ApplicationState) =>
  selectFromSelectedReport(instanceKey)(state, r => r?.sort);
